import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

interface AirbnbStyleCalendarProps {
    startDate?: Date;
    endDate?: Date;
    onDateChange: (startDate: Date, endDate: Date) => void;
    onSelectionComplete?: (startDate: Date, endDate: Date) => void;
    minDate?: Date;
}

const AirbnbStyleCalendar: React.FC<AirbnbStyleCalendarProps> = ({
    startDate,
    endDate,
    onDateChange,
    onSelectionComplete,
    minDate = new Date()
}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedStart, setSelectedStart] = useState<Date | null>(startDate || null);
    const [selectedEnd, setSelectedEnd] = useState<Date | null>(endDate || null);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    // Intentionally do not call onDateChange in an effect to avoid update loops

    // Reset internal state when props are cleared
    useEffect(() => {
        if (!startDate && !endDate) {
            setSelectedStart(null);
            setSelectedEnd(null);
        } else {
            setSelectedStart(startDate || null);
            setSelectedEnd(endDate || null);
        }
    }, [startDate, endDate]);

    const getNextMonth = (date: Date) => {
        const nextMonth = new Date(date);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        return nextMonth;
    };

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for previous month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Add days of current month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const handleDateClick = (date: Date) => {
        if (!selectedStart || (selectedStart && selectedEnd)) {
            // Start new selection
            setSelectedStart(date);
            setSelectedEnd(null);
        } else if (selectedStart && !selectedEnd) {
            // Complete selection
            let newStart, newEnd;
            if (date < selectedStart) {
                newStart = date;
                newEnd = selectedStart;
            } else {
                newStart = selectedStart;
                newEnd = date;
            }
            setSelectedStart(newStart);
            setSelectedEnd(newEnd);
            // Notify parent on completed selection
            onDateChange(newStart, newEnd);
            
            // Call completion callback if provided
            if (onSelectionComplete && newStart.getTime() !== newEnd.getTime()) {
                onSelectionComplete(newStart, newEnd);
            }
        }
    };



    const isDateInRange = (date: Date) => {
        if (!selectedStart) return false;
        if (!selectedEnd && !hoverDate) return false;
        
        const rangeEnd = selectedEnd || hoverDate;
        if (!rangeEnd) return false;

        const start = selectedStart < rangeEnd ? selectedStart : rangeEnd;
        const end = selectedStart < rangeEnd ? rangeEnd : selectedStart;
        
        return date >= start && date <= end;
    };

    const isDateSelected = (date: Date) => {
        if (!selectedStart) return false;
        if (selectedStart.toDateString() === date.toDateString()) return true;
        if (selectedEnd && selectedEnd.toDateString() === date.toDateString()) return true;
        return false;
    };

    const isDateDisabled = (date: Date) => {
        return date < minDate;
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + (direction === 'next' ? 1 : -1));
        setCurrentMonth(newMonth);
    };

    const renderMonth = (monthDate: Date) => {
        const days = getDaysInMonth(monthDate);
        const monthName = monthNames[monthDate.getMonth()];
        const year = monthDate.getFullYear();

        return (
            <View style={styles.monthContainer}>
                <Text style={styles.monthHeader}>
                    {monthName} {year}
                </Text>
                
                <View style={styles.weekDaysContainer}>
                    {weekDays.map((day, index) => (
                        <Text key={index} style={styles.weekDay}>
                            {day}
                        </Text>
                    ))}
                </View>
                
                <View style={styles.daysGrid}>
                    {days.map((date, index) => {
                        if (!date) {
                            return <View key={index} style={styles.emptyDay} />;
                        }

                        const isDisabled = isDateDisabled(date);
                        const isSelected = isDateSelected(date);
                        const isInRange = isDateInRange(date);
                        const isToday = date.toDateString() === new Date().toDateString();

                        return (
                            <Pressable
                                key={index}
                                style={[
                                    styles.dayButton,
                                    isSelected && styles.selectedDay,
                                    isInRange && !isSelected && styles.rangeDay,
                                    isDisabled && styles.disabledDay,
                                    isToday && styles.todayDay
                                ]}
                                onPress={() => !isDisabled && handleDateClick(date)}
                                onHoverIn={() => !isDisabled && setHoverDate(date)}
                                onHoverOut={() => setHoverDate(null)}
                                disabled={isDisabled}
                            >
                                <Text style={[
                                    styles.dayText,
                                    isSelected && styles.selectedDayText,
                                    isDisabled && styles.disabledDayText,
                                    isToday && styles.todayDayText
                                ]}>
                                    {date.getDate()}
                                </Text>
                            </Pressable>
                        );
                    })}
                </View>
            </View>
        );
    };

    return (
        <View style={styles.container}>
            {/* Navigation */}
            <View style={styles.navigation}>
                <Pressable onPress={() => navigateMonth('prev')} style={styles.navButton}>
                    <MaterialCommunityIcons name="chevron-left" size={24} color="#222" />
                </Pressable>
                <Pressable onPress={() => navigateMonth('next')} style={styles.navButton}>
                    <MaterialCommunityIcons name="chevron-right" size={24} color="#222" />
                </Pressable>
            </View>

            {/* Dual Month View */}
            <View style={styles.monthsContainer}>
                {renderMonth(currentMonth)}
                {renderMonth(getNextMonth(currentMonth))}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
    },
    navigation: {
        position: 'absolute',
        top: 24,
        right: 24,
        flexDirection: 'row',
        gap: 8,
        zIndex: 10,
    },
    navButton: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: 'white',
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    monthsContainer: {
        flexDirection: 'row',
        gap: 40,
    },
    monthContainer: {
        flex: 1,
    },
    monthHeader: {
        fontSize: 16,
        fontWeight: '600',
        color: '#222',
        marginBottom: 16,
        textAlign: 'center',
    },
    weekDaysContainer: {
        flexDirection: 'row',
        marginBottom: 8,
    },
    weekDay: {
        flex: 1,
        textAlign: 'center',
        fontSize: 12,
        fontWeight: '500',
        color: '#717171',
        paddingVertical: 8,
    },
    daysGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    emptyDay: {
        width: '14.28%',
        height: 40,
    },
    dayButton: {
        width: '14.28%',
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 20,
    },
    selectedDay: {
        backgroundColor: '#222',
    },
    rangeDay: {
        backgroundColor: '#f7f7f7',
    },
    disabledDay: {
        opacity: 0.3,
    },
    todayDay: {
        borderWidth: 1,
        borderColor: '#222',
    },
    dayText: {
        fontSize: 14,
        fontWeight: '400',
        color: '#222',
    },
    selectedDayText: {
        color: 'white',
        fontWeight: '600',
    },
    disabledDayText: {
        color: '#bcbcbc',
    },
    todayDayText: {
        fontWeight: '600',
    },
});

// Utility function for formatting date ranges
export const formatDateRange = (startDate?: Date, endDate?: Date): string => {
    if (!startDate || !endDate) {
        return '';
    }

    const formatOptions = { month: 'short' as const, day: 'numeric' as const };
    const formattedStart = startDate.toLocaleDateString('en-US', formatOptions);
    const formattedEnd = endDate.toLocaleDateString('en-US', formatOptions);
    return `${formattedStart} - ${formattedEnd}`;
};

export default AirbnbStyleCalendar; 